/**
 * @fileoverview Custom error classes for Sandbox Platform SDK
 */

/**
 * Base error class for all Sandbox Platform SDK errors
 */
export abstract class SandboxPlatformError extends Error {
  abstract readonly name: string
  readonly code?: string
  readonly statusCode?: number

  constructor(message: string, code?: string, statusCode?: number) {
    super(message)
    this.code = code
    this.statusCode = statusCode

    // Ensures proper prototype chain for instanceof checks
    Object.setPrototypeOf(this, new.target.prototype)
  }
}

/**
 * Authentication related errors
 */
export class AuthenticationError extends SandboxPlatformError {
  readonly name = 'AuthenticationError'

  constructor(message: string = 'Authentication failed', code?: string) {
    super(message, code, 401)
  }

  static invalidApiKey(): AuthenticationError {
    return new AuthenticationError('Invalid API key provided', 'INVALID_API_KEY')
  }

  static missingApiKey(): AuthenticationError {
    return new AuthenticationError('API key is required but not provided', 'MISSING_API_KEY')
  }

  static expiredToken(): AuthenticationError {
    return new AuthenticationError('Authentication token has expired', 'EXPIRED_TOKEN')
  }
}

/**
 * Network and connectivity related errors
 */
export class NetworkError extends SandboxPlatformError {
  readonly name = 'NetworkError'

  constructor(message: string, code?: string, statusCode?: number) {
    super(message, code, statusCode)
  }

  static timeout(): NetworkError {
    return new NetworkError('Request timed out', 'TIMEOUT', 408)
  }

  static connectionFailed(): NetworkError {
    return new NetworkError('Failed to connect to Sandbox Platform API', 'CONNECTION_FAILED')
  }

  static serverError(statusCode: number): NetworkError {
    return new NetworkError(`Server error (${statusCode})`, 'SERVER_ERROR', statusCode)
  }
}

/**
 * Sandbox related errors
 */
export class SandboxError extends SandboxPlatformError {
  readonly name = 'SandboxError'

  constructor(message: string, code?: string, statusCode?: number) {
    super(message, code, statusCode)
  }

  static notFound(sandboxId: string): SandboxError {
    return new SandboxError(`Sandbox not found: ${sandboxId}`, 'SANDBOX_NOT_FOUND', 404)
  }

  static creationFailed(reason?: string): SandboxError {
    const message = reason
      ? `Sandbox creation failed: ${reason}`
      : 'Sandbox creation failed'
    return new SandboxError(message, 'CREATION_FAILED')
  }

  static executionFailed(reason?: string): SandboxError {
    const message = reason
      ? `Code execution failed: ${reason}`
      : 'Code execution failed'
    return new SandboxError(message, 'EXECUTION_FAILED')
  }

  static invalidStatus(expectedStatus: string, actualStatus: string): SandboxError {
    return new SandboxError(
      `Invalid sandbox status. Expected: ${expectedStatus}, Actual: ${actualStatus}`,
      'INVALID_STATUS'
    )
  }

  static timeout(operation: string, timeoutSeconds: number): SandboxError {
    return new SandboxError(
      `${operation} timed out after ${timeoutSeconds} seconds`,
      'OPERATION_TIMEOUT'
    )
  }
}

/**
 * File operation related errors
 */
export class FileError extends SandboxPlatformError {
  readonly name = 'FileError'

  constructor(message: string, code?: string, statusCode?: number) {
    super(message, code, statusCode)
  }

  static notFound(filepath: string): FileError {
    return new FileError(`File not found: ${filepath}`, 'FILE_NOT_FOUND', 404)
  }

  static uploadFailed(filepath: string, reason?: string): FileError {
    const message = reason
      ? `Failed to upload file ${filepath}: ${reason}`
      : `Failed to upload file: ${filepath}`
    return new FileError(message, 'UPLOAD_FAILED')
  }

  static downloadFailed(filepath: string, reason?: string): FileError {
    const message = reason
      ? `Failed to download file ${filepath}: ${reason}`
      : `Failed to download file: ${filepath}`
    return new FileError(message, 'DOWNLOAD_FAILED')
  }

  static invalidPath(filepath: string): FileError {
    return new FileError(`Invalid file path: ${filepath}`, 'INVALID_PATH')
  }
}

/**
 * Configuration and validation errors
 */
export class ValidationError extends SandboxPlatformError {
  readonly name = 'ValidationError'

  constructor(message: string, code?: string) {
    super(message, code, 400)
  }

  static invalidTechStack(techStack: string): ValidationError {
    return new ValidationError(
      `Invalid technology stack: ${techStack}`,
      'INVALID_TECH_STACK'
    )
  }

  static invalidConfig(field: string, reason?: string): ValidationError {
    const message = reason
      ? `Invalid configuration for ${field}: ${reason}`
      : `Invalid configuration for ${field}`
    return new ValidationError(message, 'INVALID_CONFIG')
  }

  static missingRequiredField(field: string): ValidationError {
    return new ValidationError(
      `Required field is missing: ${field}`,
      'MISSING_REQUIRED_FIELD'
    )
  }
}

/**
 * Rate limiting errors
 */
export class RateLimitError extends SandboxPlatformError {
  readonly name = 'RateLimitError'
  readonly retryAfter?: number

  constructor(message: string = 'Rate limit exceeded', retryAfter?: number) {
    super(message, 'RATE_LIMIT_EXCEEDED', 429)
    this.retryAfter = retryAfter
  }
}

/**
 * WebSocket related errors
 */
export class WebSocketError extends SandboxPlatformError {
  readonly name = 'WebSocketError'

  constructor(message: string, code?: string) {
    super(message, code)
  }

  static connectionFailed(): WebSocketError {
    return new WebSocketError('WebSocket connection failed', 'WS_CONNECTION_FAILED')
  }

  static authenticationFailed(): WebSocketError {
    return new WebSocketError('WebSocket authentication failed', 'WS_AUTH_FAILED')
  }

  static unexpectedClose(): WebSocketError {
    return new WebSocketError('WebSocket connection closed unexpectedly', 'WS_UNEXPECTED_CLOSE')
  }
}

/**
 * Utility function to create appropriate error from API response
 */
export function createErrorFromResponse(response: any, statusCode?: number): SandboxPlatformError {
  const message = response?.error || response?.message || 'Unknown error'
  const code = response?.code

  if (statusCode === 401) {
    return new AuthenticationError(message, code)
  }

  if (statusCode === 404) {
    return new SandboxError(message, code, statusCode)
  }

  if (statusCode === 429) {
    const retryAfter = response?.retryAfter
    return new RateLimitError(message, retryAfter)
  }

  if (statusCode && statusCode >= 500) {
    return new NetworkError(message, code, statusCode)
  }

  if (statusCode && statusCode >= 400) {
    return new ValidationError(message, code)
  }

  return new SandboxError(message, code, statusCode)
}

/**
 * Type guard to check if error is a Sandbox Platform error
 */
export function isSandboxPlatformError(error: any): error is SandboxPlatformError {
  return error instanceof SandboxPlatformError
}